/**
 * Auth helpers (PLAN-Z M9): username-first accounts with optional email.
 * Login accepts username or email — a username resolves through the
 * SECURITY DEFINER `email_for_username` RPC (exact match only, so accounts
 * are not enumerable by prefix). All errors are generic.
 */
import { useEffect, useState } from 'react'
import { looksLikeEmail, syntheticEmail, usernameValid } from '../data/merge.ts'
import { getSupabase } from '../data/supabase.ts'
import { syncNow } from '../data/sync.ts'

export interface AuthInfo {
  userId: string
  username: string | null
  email: string | null
}

export interface AuthState {
  loading: boolean
  configured: boolean
  user: AuthInfo | null
}

export function useAuthUser(): AuthState & { refresh: () => void } {
  const sb = getSupabase()
  const configured = sb !== null
  const [auth, setAuth] = useState<{ loading: boolean; user: AuthInfo | null }>({
    loading: configured,
    user: null,
  })

  useEffect(() => {
    if (!sb) return
    let cancelled = false
    const load = async (): Promise<void> => {
      const { data } = await sb.auth.getUser()
      const u = data?.user
      if (!u) {
        if (!cancelled) setAuth({ loading: false, user: null })
        return
      }
      const { data: profile } = await sb
        .from('profiles')
        .select('username')
        .eq('id', u.id)
        .single()
      if (!cancelled) {
        setAuth({
          loading: false,
          user: { userId: u.id, username: profile?.username ?? null, email: u.email ?? null },
        })
      }
    }
    void load()
    const { data: sub } = sb.auth.onAuthStateChange(() => void load())
    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [sb])

  const refresh = () => {
    if (!sb) return
    // re-run the profile fetch outside the effect (a cancelled flag from a
    // previous pass must not swallow this)
    void (async () => {
      const { data } = await sb.auth.getUser()
      const u = data?.user
      if (!u) return
      const { data: profile } = await sb
        .from('profiles')
        .select('username')
        .eq('id', u.id)
        .single()
      setAuth({
        loading: false,
        user: { userId: u.id, username: profile?.username ?? null, email: u.email ?? null },
      })
    })()
  }

  return { loading: auth.loading, configured, user: auth.user, refresh }
}

export interface AuthResult {
  ok: boolean
  error?: string
  /** Set when the account exists but is not signed in (email confirmation on). */
  needsConfirmation?: boolean
}

export async function register(
  username: string,
  password: string,
  email: string,
): Promise<AuthResult> {
  const sb = getSupabase()
  if (!sb) return { ok: false, error: 'Sync is not configured on this deployment.' }
  if (!usernameValid(username)) {
    return { ok: false, error: 'Username must be 3–20 characters: a–z, 0–9, _ or -.' }
  }
  if (password.length < 8) {
    return { ok: false, error: 'Password must be at least 8 characters.' }
  }
  const address = email || syntheticEmail(username)
  const { data, error } = await sb.auth.signUp({ email: address, password })
  if (error) return { ok: false, error: friendlyError(error.message) }
  if (data.user) {
    const { error: profileError } = await sb
      .from('profiles')
      .upsert({ id: data.user.id, username })
    if (profileError) return { ok: false, error: profileError.message }
  }
  if (!data.session) return { ok: true, needsConfirmation: true }
  void syncNow()
  return { ok: true }
}

export async function login(input: string, password: string): Promise<AuthResult> {
  const sb = getSupabase()
  if (!sb) return { ok: false, error: 'Sync is not configured on this deployment.' }
  let email: string | null
  if (looksLikeEmail(input)) {
    email = input
  } else {
    if (!usernameValid(input)) return { ok: false, error: 'Unknown username or wrong password.' }
    const { data } = await sb.rpc('email_for_username', { p_username: input })
    email = (data as string | null) ?? null
  }
  if (!email) return { ok: false, error: 'Unknown username or wrong password.' }
  const { error } = await sb.auth.signInWithPassword({ email, password })
  if (error) return { ok: false, error: 'Unknown username or wrong password.' }
  void syncNow()
  return { ok: true }
}

export async function addEmail(email: string): Promise<AuthResult> {
  const sb = getSupabase()
  if (!sb) return { ok: false, error: 'Sync is not configured on this deployment.' }
  if (!looksLikeEmail(email) || email.endsWith('@users.dmat-prep.app')) {
    return { ok: false, error: 'Enter a real email address.' }
  }
  const { error } = await sb.auth.updateUser({ email })
  if (error) return { ok: false, error: error.message }
  return { ok: true }
}

export async function signOut(): Promise<void> {
  const sb = getSupabase()
  if (!sb) return
  await sb.auth.signOut()
}

function friendlyError(message: string): string {
  if (message.includes('already registered')) return 'That email is already registered.'
  if (message.includes('at least 6')) return 'Password must be at least 8 characters.'
  return message
}
